import { useContext } from 'react';
import { useQuery } from '@apollo/client/react';

import {
    IOfferEventDetailFragment,
    IOfferEventSubscriptionStatus,
    IOfferEventType,
    IOfferEventEnrollmentCurrentUserFragment,
    CurrentUserDocument,
} from 'graphql/types';
import {
    OfferEventPermissionsContext,
    OfferPermissionsContext,
} from 'offer/contexts/OfferPermissionsContext';
import {
    filterExtraCategoryValuesForOfferEvent,
    getIsOfferEventSubscribable,
    getIsOfferEventUnsubscribable,
    getIsUnenrollClosed,
} from 'offer/utils/offerEvent';
import { useOfferEventProgress } from 'offer/hooks/useOfferEventProgress';

interface IOfferEventDetailData {
    currentUserEnrollment?: IOfferEventEnrollmentCurrentUserFragment;
    showSubscriptionStatusSection: boolean;
    showBasicInfoSection: boolean;
    showDescriptionSection: boolean;
    showSubscriptionsSection: boolean;
    showSubcribeSection: boolean;
    showUnsubscribeSection: boolean;
    canSubscribeViaItem: boolean;
    canSeeGroupConditions: boolean;
    showProgressSection: boolean;
    unenrollClosed: boolean;
}

interface IProps {
    offerEvent: IOfferEventDetailFragment;
}

export const useOfferEventDetail = ({
    offerEvent,
}: IProps): IOfferEventDetailData => {
    const { canUpdate = false } = useContext(OfferPermissionsContext) || {};
    const { canManageSubscriptions = false } =
        useContext(OfferEventPermissionsContext) || {};
    const { data: currentUserData } = useQuery(CurrentUserDocument);

    const currentUser = currentUserData?.currentUser;

    const {
        accessTrainings,
        accessExternalContents,
        accessCourses,
        currentUserEnrollment,
        descriptionBeforeSubscription,
        descriptionAfterSubscription,
        trainers: trainerConnection,
        type,
        showParticipantList,
        publicParticipantList,
        firstDate,
        offer: { extraCategoryValues, groupConditions },
        scoring,
        accessScorms,
        accessVideos,
        certificate,
    } = offerEvent;

    const { webinarLink } = firstDate || {};

    const trainers =
        trainerConnection?.edges.map((edge) => edge?.node).filter(Boolean) ||
        [];

    const isMeeting = type === IOfferEventType.Meeting;
    const isWebinar = type === IOfferEventType.Webinar;

    const isEnrolled =
        !!currentUserEnrollment &&
        [
            IOfferEventSubscriptionStatus.Enrolled,
            IOfferEventSubscriptionStatus.Completed,
            IOfferEventSubscriptionStatus.Absent,
        ].includes(currentUserEnrollment.status);

    const trainings = accessTrainings.edges;
    const trainingsCount = trainings.length;
    const hasTrainings = !!trainingsCount;

    const externalContents = accessExternalContents.edges;
    const externalContentsCount = externalContents.length;
    const hasExternalContents = !!externalContentsCount;

    const courses = accessCourses.edges;
    const coursesCount = courses.length;
    const hasCourses = !!coursesCount;

    const scorms = accessScorms.edges;
    const scormsCount = scorms.length;
    const hasScorms = !!scormsCount;

    const videos = accessVideos.edges;
    const videosCount = videos.length;
    const hasVideos = !!videosCount;

    // Count all items to check if we can subscribe via the item
    const itemsCount =
        trainingsCount +
        externalContentsCount +
        coursesCount +
        scormsCount +
        videosCount;
    const canSubscribeViaItem =
        itemsCount === 1 && !isWebinar && !isMeeting && !canManageSubscriptions;

    const isTrainer = !!trainers.some(
        (trainer) => trainer.id === currentUser?.id
    );

    const isSubscribable = getIsOfferEventSubscribable(
        offerEvent,
        canUpdate,
        canManageSubscriptions
    );

    const showSubscriptionStatusSection = !!(
        currentUserEnrollment && !canManageSubscriptions
    );

    // Check if offer event has extra category values after filtering
    const hasExtraCategoryValues =
        !!extraCategoryValues?.length &&
        !!filterExtraCategoryValuesForOfferEvent(extraCategoryValues).length;

    const canSeeGroupConditions =
        !!groupConditions?.length && canManageSubscriptions;

    // Check if BasicInfoSection would have actual content to render
    const showBasicInfoSection =
        showSubscriptionStatusSection ||
        !!scoring?.length ||
        !!trainers.length ||
        hasExtraCategoryValues ||
        !!certificate ||
        canSeeGroupConditions;

    const canViewWebinar =
        (isWebinar || isMeeting) && (isEnrolled || canUpdate || isTrainer);

    const showItemsList =
        hasTrainings ||
        hasExternalContents ||
        hasCourses ||
        (canViewWebinar && !!webinarLink) ||
        hasScorms ||
        hasVideos;

    const hasDescription =
        !!descriptionBeforeSubscription ||
        ((isEnrolled || canUpdate) && !!descriptionAfterSubscription);

    const showDescriptionSection = hasDescription || showItemsList;

    // If a user can update or is a trainer, the user should also be able to see everything a subscribed
    // user sees
    const showSubscriptionsSection =
        canUpdate ||
        canManageSubscriptions ||
        (isEnrolled && showParticipantList) ||
        publicParticipantList;

    const showSubcribeSection = isSubscribable && !canSubscribeViaItem;

    const showUnsubscribeSection =
        currentUserEnrollment?.status ===
            IOfferEventSubscriptionStatus.Enrolled &&
        getIsOfferEventUnsubscribable(canUpdate, canManageSubscriptions);

    const unenrollClosed = getIsUnenrollClosed(offerEvent);

    const { showProgressSection } = useOfferEventProgress(offerEvent);

    return {
        currentUserEnrollment: currentUserEnrollment || undefined,
        showSubscriptionStatusSection,
        showBasicInfoSection,
        showDescriptionSection,
        showSubscriptionsSection,
        showSubcribeSection,
        showUnsubscribeSection,
        canSubscribeViaItem,
        canSeeGroupConditions,
        showProgressSection,
        unenrollClosed,
    };
};
